/**
 * Hand — the local player's hand. Owner: Phase-3 A3.2.
 *
 * Renders `view.yourHand` in array order. The engine already sorted the hand
 * for display per RULES §3.2 (group by suit, trump distinguished, within suit
 * high→low, jokers grouped at the strong end); the client MUST NOT re-sort or
 * it would diverge from the server's view.
 *
 * Legal-move highlighting (PRD FR-G3, anti-cheat by construction): a card is
 * playable iff a `play` move targeting its `cardId` exists in
 * `view.legalMoves`. The server is the oracle (RULES §5.2 follow-suit /
 * §5.4 trump-led / §6 no-lead-joker); the client only consults the move list
 * — it never computes legality itself. Illegal cards render `<Card dim />`
 * with `aria-disabled` and are non-interactive.
 *
 * Keyboard interaction (PRD §7 accessibility): the hand is a focus group.
 * ArrowLeft/ArrowRight (and ArrowUp/ArrowDown for vertical fan on phone)
 * move focus between cards; Enter/Space plays the focused card if legal.
 * Roving tabindex pattern — the focused card has `tabIndex=0`, all others
 * `tabIndex=-1`.
 *
 * If `view.yourHand` is empty, render an empty hand (no children) — the
 * binding contract guarantees a view exists by the time this component is
 * mounted (Table.tsx already guards the loading case).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Move } from "@hookum/protocol";
import { useView } from "../state/hooks.js";
import { actions } from "../wire/index.js";
import { Card } from "./Card.js";

/** Build a set of cardIds that have a `play` move in `legalMoves`. */
function legalCardIds(legalMoves: ReadonlyArray<Move>): Set<string> {
  const ids = new Set<string>();
  for (const m of legalMoves) {
    if (m.type === "play") ids.add(m.cardId);
  }
  return ids;
}

export function Hand(): JSX.Element {
  const view = useView();
  const [focusIndex, setFocusIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const hand = view?.yourHand ?? [];
  const legalIds = useMemo(
    () => (view ? legalCardIds(view.legalMoves) : new Set<string>()),
    [view],
  );
  // Phase-4 A4.5 (PRD FR-U2, RULES §3.2): emphasize trump-suit cards in the
  // hand. The engine already groups by suit and distinguishes trump in sort
  // order; this adds the visual cue Card.tsx renders. Null during bidding
  // (trump not yet declared) → no card lights up, which is the right answer.
  const trump = view?.trump ?? null;

  // Clamp focusIndex when the hand shrinks (e.g. after playing a card).
  useEffect(() => {
    if (hand.length === 0) {
      if (focusIndex !== 0) setFocusIndex(0);
      return;
    }
    if (focusIndex >= hand.length) {
      setFocusIndex(hand.length - 1);
    }
  }, [hand.length, focusIndex]);

  const onPlay = useCallback((cardId: string): void => {
    actions.playCard(cardId);
  }, []);

  // Roving-tabindex management: keep exactly one `tabindex=0` element in the
  // hand (the focused slot) and `-1` everywhere else.
  // v1.4.1 Phase 21.6 — deps array added. The effect only needs to re-run
  // when the focused index moves OR when the DOM card count changes (cards
  // added/removed). `hand` itself is recomputed each render (`view?.yourHand
  // ?? []` creates a fresh array), so depending on `hand` would defeat the
  // purpose; `hand.length` is the stable scalar that captures "cards were
  // added or removed" — the only condition that introduces new `.card`
  // nodes needing tabindex initialization. Reordering of the same N cards
  // (engine sort) keeps the same DOM nodes and still produces the right
  // tabIndex via `focusIndex`.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cards = root.querySelectorAll<HTMLElement>(".card");
    cards.forEach((el, i) => {
      el.tabIndex = i === focusIndex ? 0 : -1;
    });
  }, [hand.length, focusIndex]);

  const moveFocusTo = useCallback((next: number): void => {
    setFocusIndex(next);
    const root = rootRef.current;
    if (!root) return;
    const cards = root.querySelectorAll<HTMLElement>(".card");
    const el = cards[next];
    if (el) el.focus();
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (hand.length === 0) return;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown": {
          e.preventDefault();
          moveFocusTo(Math.min(hand.length - 1, focusIndex + 1));
          return;
        }
        case "ArrowLeft":
        case "ArrowUp": {
          e.preventDefault();
          moveFocusTo(Math.max(0, focusIndex - 1));
          return;
        }
        case "Home": {
          e.preventDefault();
          moveFocusTo(0);
          return;
        }
        case "End": {
          e.preventDefault();
          moveFocusTo(hand.length - 1);
          return;
        }
        case "Enter":
        case " ": {
          const card = hand[focusIndex];
          if (!card) return;
          if (legalIds.has(card.id)) {
            e.preventDefault();
            onPlay(card.id);
          }
          return;
        }
        default:
          return;
      }
    },
    [hand, focusIndex, legalIds, moveFocusTo, onPlay],
  );

  if (!view) return <div className="hand hand-empty" aria-label="Your hand" />;

  return (
    <div
      ref={rootRef}
      className="hand"
      role="group"
      aria-label="Your hand"
      onKeyDown={onKeyDown}
    >
      {hand.map((card, i) => {
        const legal = legalIds.has(card.id);
        const isFocused = i === focusIndex;
        const handleClick = legal ? () => onPlay(card.id) : undefined;
        const isTrump =
          trump !== null && card.kind === "pip" && card.suit === trump;
        // Phase 11 (v1.2): per-card stagger so the deal reads as a shuffle —
        // cards cascade left-to-right at 40 ms intervals. Motion's `delay`
        // applies only to the entrance properties (opacity/y/scale) via the
        // per-property `transition` object inside Card.tsx; layout/play
        // transitions ignore it, so playing a card still snaps responsively.
        const enterDelay = i * 0.04;
        // v1.4.1 Phase 21.1: round-namespaced React key. cardIds are stable
        // across rounds (e.g. "S14" always means the ace of spades), so a
        // raw `key={card.id}` makes React reconcile a round-1 card with a
        // round-2 card of the same id — which skips Card.tsx's `initial`
        // entrance animation (Phase 11 deal cascade) on every round after
        // the first. Namespacing on `view.round` forces a fresh mount each
        // round so the deal cascade fires every round.
        const slotKey = `${view.round}-${card.id}`;
        return (
          <span
            key={slotKey}
            className={
              "hand-slot" +
              (legal ? " hand-slot-legal" : " hand-slot-illegal") +
              (isFocused ? " hand-slot-focused" : "") +
              (isTrump ? " hand-slot-trump" : "")
            }
          >
            <Card
              card={card}
              dim={!legal}
              isTrump={isTrump}
              onClick={handleClick}
              enterDelay={enterDelay}
            />
          </span>
        );
      })}
    </div>
  );
}
